import { t } from 'i18next';
import { useState } from 'react';
import ProfileAvatar from '../../../components/thumbnails/ProfileAvatar';
import type { BaseFootprint } from '../../../domain/models/locations/BaseFootprint';
import { ReactComponent as AlbumIcon } from '../../../icons/album.fill.svg';
import LinkPreviewBox from '../link-preview/LinkPreviewBox';
import FootprintDetailModal from './FootprintDetailModal';

interface FootprintsBoxProps {
  locationId: string;
  footprints: BaseFootprint[];
}

const FootprintsBox: React.FC<FootprintsBoxProps> = ({ locationId, footprints }) => {
  const [selectedFootprint, setSelectedFootprint] = useState<BaseFootprint | null>(null);

  if (!footprints || footprints.length === 0) {
    return null;
  }

  return (
    <>
      <div
        className="rounded-[12px] mx-[20px]"
        style={{
          padding: '2px',
          paddingTop: '32px',
          background: 'linear-gradient(to right bottom, #15CCCB, #4987FF, #8B1CFA)'
        }}
      >
        <div className="flex justify-between items-center px-[10px] mt-[-32px] h-[32px]">
          <h3 className="font-bold text-[14px] text-white">{t('FootprintsBox.title')}</h3>
        </div>
        <div className="flex flex-col px-[18px] bg-white dark:bg-background-elevated rounded-b-[12px]">
          {footprints.slice(0, 2).map((footprint) => (
            <button
              type="button"
              key={footprint.footprintId}
              className="w-full max-w-full py-[16px] cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors border-b border-separator1"
              onClick={() => setSelectedFootprint(footprint)}
            >
              <div className="flex flex-row items-center gap-[11px]">
                <div
                  className={
                    'rounded-[7px] w-[56px] aspect-[56/44] flex items-center justify-center bg-gray-300'
                  }
                >
                  {footprint.baseThumbnailUrl ? (
                    <img
                      className="rounded-[7px] h-[44px] aspect-[56/44] object-cover"
                      src={footprint.baseThumbnailUrl}
                      alt={footprint.reporter?.nickname}
                    />
                  ) : (
                    <AlbumIcon className="w-[16px] h-[16px] fill-sub-light-2 dark:fill-sub-dark-2" />
                  )}
                </div>

                <div className="flex flex-col w-full max-w-[calc(100%-67px)]">
                  <div className="flex flex-row justify-between items-center">
                    <span className="text-[15px] font-semibold">
                      {footprint.artistName} {footprint.footprinterName}
                    </span>
                    {footprint.reporter && (
                      <div className="border-[1px] rounded-[7px] px-[6px] py-[5px]">
                        <div className="flex flex-row gap-[4px] items-center">
                          <ProfileAvatar
                            profileImageUrl={
                              footprint.reporter.thumbnailUrl ??
                              '/assets/icons/thumbnail.ghost.profile.png'
                            }
                            widthAndHeight={'14px'}
                            onClick={() => {}}
                          />
                          <span className="font-normal text-[12px] leading-[14px] text-main-light-4 dark:text-main-dark-4">
                            {footprint.reporter.nickname}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  <span className="font-normal text-[13px] text-left text-text-main-lv3 truncate">
                    {footprint.description}
                  </span>
                </div>
              </div>
              {footprint.linkUrl && (
                <div className="mt-[5px]">
                  <LinkPreviewBox url={footprint.linkUrl} />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      <FootprintDetailModal
        footprint={selectedFootprint}
        isOpen={!!selectedFootprint}
        onClose={() => setSelectedFootprint(null)}
      />
    </>
  );
};

export default FootprintsBox;
